import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Tab, Tabs, Typography, Button, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OperatorCallLeads from './OperatorCallLeads';
import OldSiteBills from './OldSiteBills';
import Enquiries from './Enquiries';
import { isLocalInternalHost } from '../../utils/localInternalHost';

const OLD_LEADS_ADMIN_PHONES = new Set(['9885750476']);

function canAccessOldLeadsUi() {
  try {
    const raw = localStorage.getItem('admin_user');
    if (!raw) return false;
    const user = JSON.parse(raw);
    const phone = String(user?.phone || '').replace(/\D/g, '');
    const last10 = phone.length > 10 ? phone.slice(-10) : phone;
    return OLD_LEADS_ADMIN_PHONES.has(last10);
  } catch {
    return false;
  }
}

const TAB_KEYS = ['leads', 'old', 'enquiries', 'demo'];

const LeadsManagementTabs = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showOldLeads = useMemo(
    () => isLocalInternalHost() && canAccessOldLeadsUi(),
    [],
  );

  const initialTab = (() => {
    const tab = (searchParams.get('tab') || '').toLowerCase();
    if (tab === 'demo' || tab === 'demo-requests' || tab === 'demorequests') return 3;
    if (tab === 'enquiries') return 2;
    if (tab === 'old' && showOldLeads) return 1;
    return 0;
  })();

  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (!showOldLeads && activeTab === 1) {
      setActiveTab(0);
    }
  }, [showOldLeads, activeTab]);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
    const key = TAB_KEYS[newValue] || 'leads';
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    if (key === 'demo') {
      next.set('source', 'demo');
    } else if (key !== 'enquiries') {
      next.delete('source');
    }
    setSearchParams(next, { replace: true });
  };

  const visibleTabs = [
    { key: 'leads', label: 'Present New Leads', index: 0 },
    ...(showOldLeads ? [{ key: 'old', label: 'Old Leads', index: 1 }] : []),
    { key: 'enquiries', label: 'Enquiries', index: 2 },
    { key: 'demo', label: 'Demo Requests', index: 3 },
  ];

  // Map MUI tab strip index → logical tab index (0 leads / 1 old / 2 enquiries / 3 demo)
  const stripValue = visibleTabs.findIndex((t) => t.index === activeTab);
  const safeStripValue = stripValue >= 0 ? stripValue : 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper elevation={0} variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 2, pb: 1.5, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" fontWeight={800} gutterBottom>
              Leads Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Call leads, registered enquiries, demo requests, and (for AA) old leads.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="text"
              href="https://services.knls.in/enquiryform"
              target="_blank"
              rel="noopener noreferrer"
            >
              Public form
            </Button>
            <Button variant="outlined" onClick={() => navigate('/bills/enquiry-form')}>
              Enquiry Form
            </Button>
          </Stack>
        </Box>
        <Tabs
          value={safeStripValue}
          onChange={(event, stripIndex) => {
            const logical = visibleTabs[stripIndex]?.index ?? 0;
            handleTabChange(event, logical);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, borderTop: 1, borderColor: 'divider' }}
        >
          {visibleTabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {activeTab === 0 ? (
        <OperatorCallLeads />
      ) : activeTab === 1 && showOldLeads ? (
        <Box sx={{ bgcolor: 'background.paper' }}>
          <OldSiteBills />
        </Box>
      ) : activeTab === 3 ? (
        <Enquiries embedded defaultSourceSegment="demo" hideSourceChips />
      ) : (
        <Enquiries embedded defaultSourceSegment="new" />
      )}
    </Box>
  );
};

export default LeadsManagementTabs;
